import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { LuFlag, LuBan } from 'react-icons/lu';
import SafetyDialog, { DialogIcon, Spinner, btnSecondary, btnDanger, btnBrand, row } from './SafetyDialog';
import { REPORT_REASONS, safetyApi, apiError } from './safetyApi';

/**
 * Reportar a un usuario, como en la app móvil: lista de motivos → confirmación
 * "¿Reportar a {nombre} por "{motivo}"?" → "Gracias". Reportar no bloquea ni
 * saca del chat. Al final de la lista va "Bloquear usuario" (onRequestBlock),
 * la segunda puerta al bloqueo; se omite si ya está bloqueado.
 * En la web se agrega un campo opcional de detalles (el backend lo acepta).
 */
export default function ReportUserModal({ open, user, onClose, afterLeave, onRequestBlock }) {
  const [step, setStep] = useState('reasons'); // reasons | confirm | thanks
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setStep('reasons');
      setReason(null);
      setDetails('');
      setError('');
    }
  }, [open]);

  const name = user?.nickname || user?.name;

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      await safetyApi.report(user.id, reason.value, details);
      setStep('thanks');
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SafetyDialog open={open} onClose={onClose} busy={busy} afterLeave={afterLeave}>
      {step === 'reasons' && (
        <>
          <div className="flex items-center gap-2">
            <LuFlag className="text-lg text-red-500" />
            <Dialog.Title className="text-lg font-semibold">{name ? `Reportar a ${name}` : 'Reportar usuario'}</Dialog.Title>
          </div>
          <div className="mt-4 space-y-2">
            {REPORT_REASONS.map(r => (
              <button key={r.value} onClick={() => { setReason(r); setStep('confirm'); }} className={row}>
                {r.label}
              </button>
            ))}
          </div>
          {onRequestBlock && (
            <>
              <hr className="my-3 border-gray-200 dark:border-gray-700" />
              <button onClick={onRequestBlock} className={`${row} text-red-600 dark:text-red-400`}>
                <LuBan className="text-lg" /> Bloquear usuario
              </button>
            </>
          )}
          <div className="mt-4 flex">
            <button onClick={onClose} className={btnSecondary}>Cancelar</button>
          </div>
        </>
      )}

      {step === 'confirm' && (
        <>
          <DialogIcon icon={LuFlag} />
          <Dialog.Title className="mt-3 text-lg font-semibold">Reportar usuario</Dialog.Title>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            ¿Reportar a {name || 'este usuario'} por “{reason?.label}”?
          </p>
          <textarea
            value={details}
            onChange={e => setDetails(e.target.value.slice(0, 2000))}
            disabled={busy}
            rows={3}
            placeholder="Agrega detalles si quieres (opcional)"
            className="mt-3 w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
          />
          {error && (
            <p className="mt-2 text-sm text-red-500"><b>No se pudo enviar el reporte.</b> {error}</p>
          )}
          <div className="mt-4 flex gap-2">
            <button onClick={() => { setError(''); setStep('reasons'); }} disabled={busy} className={btnSecondary}>Cancelar</button>
            <button onClick={send} disabled={busy} className={btnDanger}>
              {busy && <Spinner />} Reportar
            </button>
          </div>
        </>
      )}

      {step === 'thanks' && (
        <>
          <Dialog.Title className="text-lg font-semibold">Gracias</Dialog.Title>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Recibimos tu reporte y lo vamos a revisar.</p>
          <div className="mt-5 flex">
            <button onClick={onClose} className={btnBrand}>Entendido</button>
          </div>
        </>
      )}
    </SafetyDialog>
  );
}
